import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Check, CalendarPlus, Share2, Clock, Hourglass, MessageCircle } from "lucide-react";
import { Screen, ScrollArea, BottomBar, PrimaryButton, SecondaryButton } from "@/components/jk/ui";
import { Card, Pill, SectionLabel, FullScreenLoader } from "@/components/jk/kit";
import { formatINR, formatDate, formatTime } from "@/lib/format";
import api from "@/lib/api";

const BookingConfirmed = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(!!sessionId);

  useEffect(() => {
    let cancelled = false;
    const loadBooking = () => api.get(`/public/bookings/${id}`)
      .then(({ data }) => { if (!cancelled) setData(data); return null; }).catch(() => {});

    const run = async () => {
      // Returning from Stripe → poll status (this finalizes the booking server-side).
      if (sessionId) {
        for (let i = 0; i < 6 && !cancelled; i++) {
          try {
            const { data: st } = await api.get(`/public/stripe/checkout-status/${sessionId}`);
            if (st.payment_status === "paid") break;
            if (st.status === "expired") break;
          } catch { /* keep trying */ }
          await new Promise((r) => setTimeout(r, 2000));
        }
        if (!cancelled) setVerifying(false);
      }
      await loadBooking();
      if (!cancelled) setLoading(false);
    };
    run();
    return () => { cancelled = true; };
  }, [id, sessionId]);

  if (loading || verifying) return <FullScreenLoader />;
  if (!data) return null;
  const b = data.booking;
  const pending = b.status !== "confirmed";

  const addToCalendar = () => {
    const start = `${(b.booking_date || "").replace(/-/g, "")}T${(b.booking_time || "00:00").replace(":", "")}00`;
    const ics = `BEGIN:VCALENDAR\nVERSION:2.0\nBEGIN:VEVENT\nSUMMARY:Consultation with ${data.astrologer.full_name || "astrologer"}\nDTSTART:${start}\nDESCRIPTION:${data.service_name} (Ref ${b.booking_reference})\nEND:VEVENT\nEND:VCALENDAR`;
    const url = "data:text/calendar;charset=utf-8," + encodeURIComponent(ics);
    const link = document.createElement("a");
    link.href = url; link.download = "consultation.ics"; link.click();
  };
  const share = async () => {
    const text = `My consultation with ${data.astrologer.full_name} on ${formatDate(b.booking_date)} at ${formatTime(b.booking_time)}. Ref #${b.booking_reference}`;
    if (navigator.share) { try { await navigator.share({ text }); } catch { /* user cancelled */ } }
  };
  const slug = data.astrologer.booking_slug;
  const bookingLink = slug ? `${window.location.origin}/${slug}` : "";
  const shareWhatsApp = () => {
    const parts = [
      `Just booked a consultation with ${data.astrologer.full_name} on JothidaKural ✨`,
      bookingLink ? `Book yours here: ${bookingLink}` : "",
    ].filter(Boolean);
    const url = `https://wa.me/?text=${encodeURIComponent(parts.join("\n\n"))}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <Screen testid="booking-confirmed-screen">
      <ScrollArea className="px-6 pt-16">
        <div className="flex flex-col items-center text-center">
          <div className={`flex h-20 w-20 items-center justify-center rounded-full ${pending ? "bg-[var(--jk-peach)]" : "bg-[#111111]"}`} data-testid="confirm-icon">
            {pending ? <Hourglass size={34} strokeWidth={1.75} className="text-[#111111]" /> : <Check size={40} strokeWidth={2.5} className="text-white" />}
          </div>
          <h1 className="mt-6 text-[28px] font-bold leading-tight tracking-[-0.02em] text-[#111111]" data-testid="confirm-title">
            {pending ? "Slot reserved" : "Booking confirmed!"}
          </h1>
          <p className="mt-2 text-[14px] leading-relaxed text-[#666666]">
            {pending
              ? `We've held your slot. ${data.astrologer.full_name || "The astrologer"} will confirm shortly — you'll get a confirmation by SMS & email.`
              : "We've sent the details to your email and SMS. See you soon!"}
          </p>
        </div>

        <Card className="mt-8" data-testid="confirm-details">
          <div className="flex items-center justify-between">
            <SectionLabel>{pending ? "Reserved booking" : "Booking details"}</SectionLabel>
            {pending
              ? <span className="flex items-center gap-1 text-[12px] text-[#a07a5e]"><Clock size={13} /> Awaiting confirmation</span>
              : <Pill tone="neutral">#{b.booking_reference}</Pill>}
          </div>
          <p className="mt-3 text-[18px] font-bold text-[#111111]">{data.service_name}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <Pill tone="periwinkle">{formatDate(b.booking_date)}</Pill>
            <Pill tone="neutral">{formatTime(b.booking_time)}</Pill>
            {b.consultation_mode && <Pill tone="neutral">{b.consultation_mode}</Pill>}
          </div>
          <div className="mt-4 flex items-center justify-between border-t border-[#f0ede8] pt-4">
            <span className="text-[13px] text-[#666666]">Amount {pending ? "due" : "paid"}</span>
            <span className="text-[16px] font-bold text-[#111111]">{formatINR(b.fee_inr)}</span>
          </div>
          {!pending && data.receipt && (
            <p className="mt-3 border-t border-[#f0ede8] pt-3 text-[11px] leading-relaxed text-[#999999]" data-testid="confirm-receipt">
              Receipt issued by {data.receipt.company_name} · GSTIN {data.receipt.company_gstin}
            </p>
          )}
        </Card>
        <div className="h-6" />
      </ScrollArea>

      {!pending && (
        <BottomBar>
          <button
            data-testid="share-whatsapp"
            onClick={shareWhatsApp}
            className="mb-3 flex w-full items-center justify-center gap-2 rounded-2xl bg-[#25D366] py-3.5 text-[15px] font-bold text-white transition-transform active:scale-[0.98]"
          >
            <MessageCircle size={18} strokeWidth={2.25} /> Share on WhatsApp
          </button>
          <div className="flex gap-3">
            <div className="flex-1"><SecondaryButton testid="add-calendar" onClick={addToCalendar} icon={<CalendarPlus size={17} strokeWidth={2} />}>Calendar</SecondaryButton></div>
            <div className="flex-1"><PrimaryButton testid="share-booking" onClick={share} icon={<Share2 size={16} strokeWidth={2} />}>Share</PrimaryButton></div>
          </div>
        </BottomBar>
      )}
    </Screen>
  );
};

export default BookingConfirmed;
